import { supabase, unwrap, fetchAllRows } from '../supabase';
import { resolveRange, type DateRangeValue } from '../dates';
import { parseLabels } from '../labels';
import { phoneQueryDigits } from '../phone';
import { archiveQueryGroup, type ArchiveVisibility } from '../archive-filter';
import type { Call, CallTab, Paged, TabCounts } from '../types';

export interface CallQuery {
  range: DateRangeValue;
  tab: CallTab;
  employeeId?: string;
  search?: string;
  archived?: ArchiveVisibility;
  type?: string;
  connection?: 'connected' | 'not_connected';
  recording?: 'with' | 'without';
  notes?: 'has_call_note' | 'no_call_note' | 'has_person_note' | 'no_person_note' | 'any_note';
  duration?: 'under_30s' | '30s_to_2m' | '2m_to_5m' | 'over_5m';
  reviewed?: 'reviewed' | 'not_reviewed';
  label?: string;
  named?: 'named' | 'unnamed';
}

export interface ContactCallScope {
  range: DateRangeValue;
  employeeId?: string;
}

export type CallSortKey = 'call_time' | 'duration' | 'caller_name' | 'employee_name';
export interface Sort<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

const toNum = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const toStr = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));

export function mapCall(row: Record<string, unknown>): Call {
  return {
    id: toNum(row.id),
    employeeId: row.employee_id === null || row.employee_id === undefined ? null : toNum(row.employee_id),
    employeeName: toStr(row.employee_name),
    phone: String(row.caller_phone ?? row.phone_number ?? ''),
    name: toStr(row.person_name ?? row.caller_name ?? row.contact_name),
    type: String(row.type || 'unknown').toLowerCase(),
    duration: toNum(row.duration),
    callTime: toStr(row.call_time),
    note: toStr(row.note),
    reviewed: row.reviewed === true || row.reviewed === 1 || row.reviewed === 't',
    recordingUrl: toStr(row.recording_url)?.trim() || null,
    fileStatus: toStr(row.file_status ?? row.upload_status),
    devicePhone: toStr(row.device_phone),
    personNote: toStr(row.person_note),
    labels: parseLabels(row.person_labels ?? row.labels),
    isArchived: Boolean(row.person_is_archived),
    isStarred: Boolean(row.person_is_starred),
    identityId:
      (row.identity_id ?? row.person_identity_id) ? toNum(row.identity_id ?? row.person_identity_id) : null,
    createdAt: toStr(row.created_at),
    updatedAt: toStr(row.updated_at),
  };
}

/** Strip characters that would break a PostgREST logic-tree expression. */
export const safeTerm = (value: string) =>
  value
    .replace(/[,()"'\\%*:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const EMPTY = (col: string) => `or(${col}.is.null,${col}.eq.)`;
const PRESENT = (col: string) => `and(${col}.not.is.null,${col}.neq.)`;

type Builder = ReturnType<ReturnType<typeof supabase.from>['select']>;

function applyCallQuery(query: Builder, orgId: string, q: CallQuery, opts: { phone?: string } = {}): Builder {
  // Every condition that needs OR logic is collected here and sent as a single
  // `or=(and(...))` so they combine with AND instead of fighting over one param.
  const groups: string[] = [];
  let b = query.eq('org_id', orgId);

  if (opts.phone) {
    b = b.eq('caller_phone', opts.phone);
  } else {
    const archiveGroup = archiveQueryGroup('person_is_archived', q.archived, q.tab);
    if (archiveGroup) groups.push(archiveGroup);
  }
  const { start, end } = resolveRange(q.range);
  if (start) b = b.gte('call_time', start.toISOString());
  if (end) b = b.lte('call_time', end.toISOString());

  if (q.employeeId) b = b.eq('employee_id', Number(q.employeeId));

  switch (q.tab) {
    case 'answered':
      b = b.eq('type', 'incoming').gte('duration', 3);
      break;
    case 'not_answered':
      groups.push('or(type.in.(missed,rejected,blocked),and(type.eq.incoming,duration.eq.0))');
      break;
    case 'outgoing':
      b = b.eq('type', 'outgoing').gte('duration', 3);
      break;
    case 'outgoing_not_connected':
      b = b.eq('type', 'outgoing').eq('duration', 0);
      break;
    case 'never_connected':
      b = b.eq('duration', 0);
      break;
    case 'not_reviewed':
      groups.push('or(reviewed.is.null,reviewed.eq.false)');
      break;
    case 'no_note':
      groups.push(EMPTY('note'), EMPTY('person_note'));
      break;
    case 'failed':
      b = b.gt('duration', 0).lt('duration', 3);
      break;
    default:
      break;
  }

  const search = q.search?.trim();
  if (search) {
    const digits = phoneQueryDigits(search);
    if (digits) {
      b = b.ilike('caller_phone', `%${digits}%`);
    } else {
      const term = safeTerm(search);
      if (term)
        groups.push(
          `or(person_name.ilike.%${term}%,caller_name.ilike.%${term}%,note.ilike.%${term}%,person_note.ilike.%${term}%,caller_phone.ilike.%${term}%)`,
        );
    }
  }

  if (q.type) b = b.eq('type', q.type);
  if (q.connection === 'connected') b = b.gt('duration', 0);
  if (q.connection === 'not_connected') b = b.eq('duration', 0);
  if (q.recording === 'with') b = b.not('recording_url', 'is', null).neq('recording_url', '');
  if (q.recording === 'without') groups.push(EMPTY('recording_url'));
  if (q.notes === 'has_call_note') b = b.not('note', 'is', null).neq('note', '');
  if (q.notes === 'no_call_note') groups.push(EMPTY('note'));
  if (q.notes === 'has_person_note') b = b.not('person_note', 'is', null).neq('person_note', '');
  if (q.notes === 'no_person_note') groups.push(EMPTY('person_note'));
  if (q.notes === 'any_note') groups.push(`or(${PRESENT('note')},${PRESENT('person_note')})`);
  if (q.duration === 'under_30s') b = b.lt('duration', 30);
  if (q.duration === '30s_to_2m') b = b.gte('duration', 30).lt('duration', 120);
  if (q.duration === '2m_to_5m') b = b.gte('duration', 120).lte('duration', 300);
  if (q.duration === 'over_5m') b = b.gt('duration', 300);
  if (q.reviewed === 'reviewed') b = b.eq('reviewed', true);
  if (q.reviewed === 'not_reviewed') groups.push('or(reviewed.is.null,reviewed.eq.false)');
  if (q.named === 'named') b = b.not('person_name', 'is', null).neq('person_name', '');
  if (q.named === 'unnamed') groups.push(EMPTY('person_name'));
  if (q.label) {
    const label = q.label.trim();
    if (label) b = b.contains('person_labels', JSON.stringify([label]));
  }

  if (groups.length === 1) b = b.or(groups[0].replace(/^or\((.*)\)$/s, '$1'));
  else if (groups.length > 1) b = b.or(`and(${groups.join(',')})`);
  return b;
}

// Every field consumed by mapCall; omit transport/device bookkeeping from repeated lists.
const CALL_COLUMNS =
  'id,employee_id,employee_name,caller_phone,caller_name,person_name,type,duration,call_time,note,reviewed,recording_url,file_status,device_phone,person_note,person_labels,person_is_archived,person_is_starred,person_identity_id,created_at,updated_at';

const SORT_COLUMNS: Record<CallSortKey, string> = {
  call_time: 'call_time',
  duration: 'duration',
  caller_name: 'person_name',
  employee_name: 'employee_name',
};

export async function listCalls(
  orgId: string,
  q: CallQuery,
  page: number,
  pageSize: number,
  sort: Sort<CallSortKey> = { key: 'call_time', dir: 'desc' },
  signal?: AbortSignal,
): Promise<Paged<Call>> {
  const from = (page - 1) * pageSize;
  let query = applyCallQuery(
    supabase.from('call_log_with_person').select(CALL_COLUMNS, { count: 'exact' }),
    orgId,
    q,
  );
  query = query
    .order(SORT_COLUMNS[sort.key] ?? 'call_time', { ascending: sort.dir === 'asc', nullsFirst: false })
    .order('id', { ascending: false })
    .range(from, from + pageSize - 1);
  if (signal) query = query.abortSignal(signal);
  const res = await query;
  const rows = unwrap(res) as Record<string, unknown>[];
  return { rows: (rows ?? []).map(mapCall), total: res.count ?? rows?.length ?? 0 };
}

export async function exportCalls(orgId: string, q: CallQuery): Promise<Call[]> {
  const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
    applyCallQuery(supabase.from('call_log_with_person').select(CALL_COLUMNS), orgId, q)
      .order('call_time', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to),
  );
  return rows.map(mapCall);
}

/** Exact-number history; dashboard drilldowns optionally retain their date and employee scope. */
export async function listContactCalls(
  orgId: string,
  phone: string,
  page: number,
  pageSize: number,
  scope?: ContactCallScope,
): Promise<Paged<Call>> {
  const from = (page - 1) * pageSize;
  const res = await applyCallQuery(
    supabase.from('call_log_with_person').select(CALL_COLUMNS, { count: 'exact' }),
    orgId,
    { range: scope?.range ?? { preset: 'all' }, tab: 'all', employeeId: scope?.employeeId },
    { phone },
  )
    .order('call_time', { ascending: false })
    .order('id', { ascending: false })
    .range(from, from + pageSize - 1);
  const rows = unwrap(res) as Record<string, unknown>[];
  return { rows: (rows ?? []).map(mapCall), total: res.count ?? 0 };
}

/** Cheap existence check used by onboarding (no count scan). */
export async function hasAnyCall(orgId: string, employeeId?: number, recording = false): Promise<boolean> {
  let query = supabase.from('call_log').select('id').eq('org_id', orgId).limit(1);
  if (employeeId !== undefined) query = query.eq('employee_id', employeeId);
  if (recording) query = query.not('recording_url', 'is', null).neq('recording_url', '');
  const rows = unwrap(await query) as unknown[];
  return Boolean(rows?.length);
}

export async function countCalls(
  orgId: string,
  range: DateRangeValue,
  employeeId?: string,
): Promise<TabCounts> {
  const { start, end } = resolveRange(range);
  const data = unwrap(
    await supabase.rpc('get_call_counts', {
      p_org_id: orgId,
      p_start_date: start ? start.toISOString() : null,
      p_end_date: end ? end.toISOString() : null,
      p_employee_id: employeeId ? Number(employeeId) : null,
    }),
  ) as Record<string, unknown> | null;
  const n = (k: string) => toNum(data?.[k]);
  return {
    all: n('all'),
    answered: n('inbound') || n('answered'),
    not_answered: n('not_answered'),
    outgoing: n('outgoing'),
    outgoing_not_connected: n('outgoing_not_connected'),
    never_connected: n('never_connected'),
    not_reviewed: n('not_reviewed'),
    no_note: n('no_note'),
    failed: n('failed'),
    excluded: n('excluded'),
  };
}

export async function updateCall(
  orgId: string,
  id: number,
  patch: { note?: string; reviewed?: boolean },
): Promise<void> {
  const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.note !== undefined) update.note = patch.note.trim();
  if (patch.reviewed !== undefined) update.reviewed = patch.reviewed;
  unwrap(await supabase.from('call_log').update(update).eq('id', id).eq('org_id', orgId));
}

export async function setReviewed(orgId: string, ids: number[], reviewed = true): Promise<void> {
  if (!ids.length) return;
  unwrap(
    await supabase
      .from('call_log')
      .update({ reviewed, updated_at: new Date().toISOString() })
      .in('id', ids)
      .eq('org_id', orgId),
  );
}

/** Mark every call with one number as reviewed, not just the page on screen. */
export async function markContactReviewed(orgId: string, phone: string): Promise<void> {
  unwrap(
    await supabase
      .from('call_log')
      .update({ reviewed: true, updated_at: new Date().toISOString() })
      .eq('org_id', orgId)
      .eq('caller_phone', phone)
      .or('reviewed.is.null,reviewed.eq.false'),
  );
}

export async function listLabels(orgId: string): Promise<string[]> {
  const rows = unwrap(
    await supabase
      .from('identities')
      .select('labels')
      .eq('org_id', orgId)
      .not('labels', 'is', null)
      .limit(5000),
  ) as { labels: unknown }[];
  const all = new Map<string, string>();
  for (const row of rows ?? [])
    for (const label of parseLabels(row.labels)) all.set(label.toLowerCase(), label);
  return [...all.values()].sort((a, b) => a.localeCompare(b));
}

export const CALL_TYPES = [
  { id: 'incoming', label: 'Incoming' },
  { id: 'outgoing', label: 'Outgoing' },
  { id: 'missed', label: 'Missed' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'blocked', label: 'Blocked' },
];
